import { applyLogic } from '@joyfill/conditional-logic';
import { joyDocHelper } from '@builttocreate/engine-utils';
import FieldTypes from '../constants/FieldTypes';
import FileViews from '../constants/FileViews';
import { flattenArray } from './collectionHelper';
import { getFileViewSelector, sortFieldPositionsByXAndYCoordinates, isFieldHiddenInView } from './documentHelper';

const { getPageOrder, sortPages } = joyDocHelper;

export const validate = (doc, options = {}) => {

  const view = options.view || FileViews.desktop;
  const defaultFile = doc?.files[0];
  const validated = { status: null, fieldValidations: [] };

  if (!defaultFile) return validated;

  /**
   * Step 1: Get the target file view from the document. Defaults to 'desktop' view.
   */
  const fileView = getFileViewSelector({ doc, view });

  const fieldValidations = [];

  /**
  * Step 2: Properly sort the pages and apply conditional logic to them.
  */
  applyLogic(
    sortPages(fileView.pages, getPageOrder(fileView.pageOrder, fileView.pages)),
    doc.fields,
    '_id'
  ).forEach((page) => {

    //2.1 Apply conditional logic to the page fields
    const logicAppliedFields = applyLogic(page.fields, doc.fields, '_id');

    //2.2 Sort page fields
    const pageFields = sortFieldPositionsByXAndYCoordinates(logicAppliedFields);

    //2.3 Validate each page field — hidden fields are excluded entirely
    pageFields.forEach((field) => {

      if (page.hidden || field.hidden || isFieldHiddenInView(field.hiddenViews, view)) {
        return;
      }

      const nextFieldValidation = validateField(field, view, doc.fields);
      nextFieldValidation.pageId = page._id;

      fieldValidations.push(nextFieldValidation);

    });

  });

  /**
   * Step 3: Check if all fields are valid to determine the overall status
   */
  const allFieldsValid = fieldValidations.every((validation) => validation.status === 'valid')

  return {
    status: allFieldsValid ? 'valid' : 'invalid',
    fieldValidations
  };

};

/**
 * valid: Field is not required OR Field is hidden OR Field value is populated.
 * invalid: Field is required AND Field is visible AND Field value is empty.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 *
 * @returns {{ field, status: 'valid' | 'invalid', reasons?: string[] }}
 *   - status 'valid'   → no `reasons` property
 *   - status 'invalid' → `reasons` array always present (may be empty)
 */
export function validateField(field, view, docFields) {

  if (field.hidden || isFieldHiddenInView(field.hiddenViews, view)) {
    return { field, status: 'valid' };
  }

  //Handle collection fields
  if (field.type === FieldTypes.collection) {
    const reasons = getCollectionFieldReasons(field, view, docFields);
    if (reasons.length > 0) {
      return { field, status: 'invalid', reasons };
    }
    return { field, status: 'valid' };
  }

  //Handle table fields
  else if (field.type === FieldTypes.table) {
    const reasons = [];

    if (field.required && isEmptyValue(field.value, field.type)) {
      reasons.push('Required');
    }

    if (isTableFieldInvalid(field, view, docFields)) {
      reasons.push('Child table is empty');
    }

    if (reasons.length > 0) {
      return { field, status: 'invalid', reasons };
    }

    return { field, status: 'valid' };
  }

  //Handle all other fields
  else {

    if (field.required && isEmptyValue(field.value, field.type)) {
      return { field, status: 'invalid', reasons: ['Required'] };
    } else {
      return { field, status: 'valid' };
    }

  }

}

/**
 * Returns an array of reason strings describing why a collection field is invalid.
 * Empty array means the field is valid.
 *
 * Collection fields need special handling because they can have nested required
 * tables and columns — we must check all nested rows for missing required values,
 * not just the top-level required flag.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 * @returns {string[]}
 */
function getCollectionFieldReasons(field, view, docFields) {
  const reasons = [];

  if (field.required && (!field.value || field.value?.length === 0 || field.value?.every((item) => item === ''))) {
    reasons.push('Required');
  }

  const flattenedArray = flattenArray(field.type, field.displayType, field.fieldPositionSchema, field.schema, field.value, field.disabled, field.mode, view, docFields);

  const invalidRowFound = flattenedArray.find((row) => row.isInvalid);
  if (invalidRowFound) reasons.push('Child table is empty');

  return reasons;
}

/**
 * Check if the collection field is empty.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 * @returns {Boolean}
 */
export function isCollectionFieldEmpty(field, view, docFields) {
  return getCollectionFieldReasons(field, view, docFields).length > 0;
}

/**
 * Check if a table field has any required columns with empty cell values.
 * 
 * A table is invalid if any visible required column has an empty cell in any row.
 * Columns that are hidden in the current view or deleted are skipped.
 * 
 * @param {Object} field - The table field object
 * @param {String} view - The current view (desktop, mobile, pdf)
 * @returns {Boolean} true if the table has required column violations
 */
export function isTableFieldInvalid(field, view, docFields) {

  const { tableColumns, value: rows } = field;

  if (!tableColumns || !Array.isArray(tableColumns)) return false;
  if (!rows || !Array.isArray(rows) || rows.length === 0) return false;

  const evaluated = docFields ? applyLogic(tableColumns, docFields, '_id') : tableColumns;

  const requiredColumns = evaluated.filter((col) => {
    if (col.deleted) return false;
    if (col.hidden) return false;
    if (isFieldHiddenInView(col.hiddenViews, view)) return false;
    return col.required;
  });

  if (requiredColumns.length === 0) return false;

  return rows.some((row) => {
    if (row.deleted) return false;
    const cells = row.cells || {};
    return requiredColumns.some((col) => {
      const cellValue = cells[col._id];
      return isEmptyCellValue(cellValue, col.type);
    });
  });

}

/**
 * Check if a table cell value is empty based on column type.
 *
 * @param {*} value
 * @param {String} columnType
 * @returns {Boolean}
 */
export function isEmptyCellValue(value, columnType) {
  if (columnType === 'image' || columnType === 'multiSelect') {
    return !Array.isArray(value) || value.length === 0 || value.every((item) => item === '');
  }
  return value === '' || value === null || value === undefined;
}

export function isEmptyValue(value, fieldType) {
  if (
    fieldType === FieldTypes.multiSelect ||
    fieldType === FieldTypes.image ||
    fieldType === FieldTypes.table ||
    fieldType === FieldTypes.file
  ) {
    return (
      !Array.isArray(value) ||
      value.length === 0 ||
      value.every((item) => item === '')
    );
  }
  return value === '' || value === null || value === undefined;
}

export default {
  validate,
  validateField,
  isTableFieldInvalid,
  isEmptyCellValue,
};
